import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import MobileNav from '../../components/mobile/MobileNav';
import MobileCard from '../../components/mobile/MobileCard';
import SyncStatusChip from '../../components/mobile/SyncStatusChip';
import {
  QrCode,
  Play,
  CheckCircle,
  ChevronRight,
  AlertTriangle,
  Loader2,
  CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';

// ---- helpers -------------------------------------------------------------
const formatTime = (seconds) => {
  if (!seconds) return '00:00:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
};

const overdueDays = (wo) => {
  if (!wo.scheduled_date || wo.status === 'completed') return null;
  const d = daysSince(wo.scheduled_date);
  return d > 0 ? d : null;
};

// ---- shared bits ---------------------------------------------------------
const SectionLabel = ({ children, right }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="text-[11px] font-bold uppercase tracking-[0.11em]" style={{ color: '#6E6A61' }}>
      {children}
    </h2>
    {right}
  </div>
);

const CHIP_TONES = {
  volt: 'bg-[var(--volt-tint)] text-[var(--volt-ink)] border-[var(--volt-edge)]',
  overdue: 'bg-[var(--overdue-bg)] text-[var(--overdue-ink)] border-[var(--overdue-edge)]',
  critical: 'bg-[var(--critical-bg)] text-[var(--critical-ink)] border-[var(--critical-edge)]',
};

const Chip = ({ tone, children }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-[4px] text-[11px] font-semibold border ${CHIP_TONES[tone]}`}
  >
    {children}
  </span>
);

// Priority chip only for Urgent / High — Normal is the default and gets no ink.
const PriorityChip = ({ priority }) => {
  const p = (priority || '').toLowerCase();
  if (p === 'urgent' || p === 'critical') return <Chip tone="critical">URGENT</Chip>;
  if (p === 'high') return <Chip tone="overdue">HIGH</Chip>;
  return null;
};


// PM/maintenance jobs are "Planned"; anything raised from a defect is a "Repair".
const workTypeLabel = (wo) => {
  const isRepair =
    (wo.linked_defect_ids && wo.linked_defect_ids.length > 0) ||
    !!wo.defect_id ||
    /^repair/i.test(wo.title || '');
  return isRepair ? 'Repair' : 'Planned';
};

const TextButton = ({ children, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="w-full h-11 text-[13px] font-semibold text-[var(--volt-ink)] mt-1"
  >
    {children}
  </button>
);

// ---- work order card -----------------------------------------------------
const WorkOrderCard = ({ workOrder }) => {
  const { formatDate } = useSettings();
  const shortDate = (d) => (d ? formatDate(d, 'dd MMM') : null);
  const [elapsed, setElapsed] = useState(workOrder.total_time_seconds || 0);
  const navigate = useNavigate();

  useEffect(() => {
    if (workOrder.timer_is_running && workOrder.timer_started_at) {
      const startedAt = new Date(workOrder.timer_started_at);
      const interval = setInterval(() => {
        const el = Math.floor((Date.now() - startedAt) / 1000) + (workOrder.total_time_seconds || 0);
        setElapsed(el);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [workOrder.timer_is_running, workOrder.timer_started_at, workOrder.total_time_seconds]);

  const od = overdueDays(workOrder);
  const isInProgress = workOrder.timer_is_running;
  const spine = isInProgress ? 'var(--volt-edge)' : od ? 'var(--overdue)' : 'var(--border)';
  const asset = workOrder.asset_unit_number || workOrder.asset_name || '—';
  const typeLabel = workTypeLabel(workOrder);

  return (
    <MobileCard
      spine={spine}
      onClick={() => navigate(`/mobile/work-orders/${workOrder.id}`)}
      className="mb-2.5"
      padding="normal"
    >
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          {/* line 1: asset ID leads, priority chip inline, timer on the right */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[14px] font-bold text-[var(--ink-primary)] truncate">{asset}</span>
              <PriorityChip priority={workOrder.priority} />
            </div>
            {isInProgress && (
              <div className="flex items-center gap-1 shrink-0">
                <Play className="w-3 h-3 fill-current text-[var(--ink-secondary)]" />
                <span className="font-mono text-[13px] text-[var(--ink-primary)]">{formatTime(elapsed)}</span>
              </div>
            )}
          </div>

          {/* line 2: job title */}
          <div className="text-[15px] font-medium text-[var(--ink-primary)] truncate mt-1">{workOrder.title}</div>

          {/* line 3: metadata — overdue dot+text folded in, then work type */}
          <div className="flex items-center gap-1.5 mt-1.5 text-[13px] min-w-0">
            {od ? (
              <>
                <span className="shrink-0 rounded-full" style={{ width: 7, height: 7, background: 'var(--overdue)' }} />
                <span className="font-semibold text-[var(--overdue-ink)] shrink-0">
                  Overdue {od} {od === 1 ? 'day' : 'days'}
                </span>
              </>
            ) : (
              <span className="text-[var(--ink-tertiary)] shrink-0">
                {workOrder.scheduled_date ? `Due ${shortDate(workOrder.scheduled_date)}` : 'No date'}
              </span>
            )}
            <span className="text-[var(--ink-tertiary)] truncate">· {typeLabel}</span>
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-[var(--ink-tertiary)] shrink-0" />
      </div>
    </MobileCard>
  );
};

// ---- inspection due card -------------------------------------------------
const InspectionDueCard = ({ asset, template }) => {
  const navigate = useNavigate();
  const go = () => navigate(`/mobile/inspections/new/${asset.id}/${template.id}`);
  return (
    <MobileCard spine="var(--volt-edge)" onClick={go} className="mb-2.5" padding="normal">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[14px] font-bold text-[var(--ink-primary)] truncate">
            {asset.unit_number || asset.name}
          </div>
          <div className="text-[13px] text-[var(--ink-secondary)] truncate mt-0.5">{template.name}</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); go(); }}
          className="shrink-0 h-9 px-4 rounded-[12px] bg-[var(--volt)] text-black text-[12px] font-bold uppercase tracking-[0.04em]"
          style={{ border: '1px solid var(--volt-edge)' }}
        >
          Start
        </button>
      </div>
    </MobileCard>
  );
};

// ---- defect row ----------------------------------------------------------
// The dot carries severity — that is its entire job.
const DEFECT_DOT = {
  critical: '#C23A00',
  high: '#C23A00',
  medium: '#E0B800',
  low: '#8A857A',
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const DefectRow = ({ defect, first }) => {
  const navigate = useNavigate();
  const sev = (defect.severity || '').toLowerCase();
  const dot = DEFECT_DOT[sev] || '#8A857A';
  const asset = defect.asset_unit_number || defect.asset_name || defect.defect_number || '—';
  const age = daysSince(defect.created_at);
  const meta = [
    asset,
    age != null ? `${age} ${age === 1 ? 'day' : 'days'} open` : null,
    sev ? cap(sev) : null,
  ].filter(Boolean).join(' · ');
  return (
    <div
      onClick={() => navigate(`/mobile/defects/${defect.id}`)}
      className={`flex items-center justify-between gap-3 py-2.5 cursor-pointer ${
        first ? '' : 'border-t border-[var(--divider)]'
      }`}
      data-testid={`dash-defect-${defect.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className="shrink-0 rounded-full"
          style={{ width: 10, height: 10, background: dot, border: '1px solid rgba(0,0,0,0.18)' }}
        />
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-[var(--ink-primary)] truncate">{defect.title}</div>
          <div className="font-mono text-[11px] text-[var(--ink-tertiary)] truncate mt-0.5">{meta}</div>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-[var(--ink-tertiary)] shrink-0" />
    </div>
  );
};

// ---- dashboard -----------------------------------------------------------
const MobileDashboard = () => {
  const { user, getToken } = useMobileAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    my_work_orders: [],
    inspections_due: [],
    recent_defects: [],
    stats: {},
  });

  const loadDashboard = async () => {
    try {
      const token = getToken();
      const response = await mobileApi.getDashboard(token);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(() => loadDashboard(), 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const activeWorkOrders = data.my_work_orders?.filter((wo) => wo.status !== 'completed') || [];
  const inProgressWorkOrders = activeWorkOrders.filter((wo) => wo.timer_is_running);
  const overdueCount = activeWorkOrders.filter((wo) => overdueDays(wo)).length;
  const openDefectCount = data.stats?.open_defects ?? data.recent_defects?.length ?? 0;
  const servicesDueCount = data.stats?.services_due ?? 0;
  const servicesOverdueCount = data.stats?.services_overdue ?? 0;
  // Worst-first: most overdue at the top.
  const pendingWorkOrders = activeWorkOrders
    .filter((wo) => !wo.timer_is_running)
    .sort((a, b) => {
      const oa = overdueDays(a) || 0;
      const ob = overdueDays(b) || 0;
      if (ob !== oa) return ob - oa;
      const da = a.scheduled_date ? new Date(a.scheduled_date).getTime() : Infinity;
      const db = b.scheduled_date ? new Date(b.scheduled_date).getTime() : Infinity;
      return da - db;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--surface-page)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-24">
      {/* Header — white band, distinct from the neutral body */}
      <div className="sticky top-0 z-30 bg-[var(--surface-card)] border-b border-[var(--border)] px-4 pt-6 pb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12.5px] text-[var(--ink-secondary)]">{getGreeting()}</p>
          <h1 className="text-[22px] font-extrabold text-[var(--ink-primary)] leading-tight truncate">
            {user?.name?.split(' ')[0] || 'User'}
          </h1>
          {user?.depot_name && (
            <p className="text-[12.5px] text-[var(--ink-tertiary)] mt-0.5">{user.depot_name}</p>
          )}
        </div>
        <SyncStatusChip />
      </div>

      {/* Primary action — the single volt fill on this screen */}
      <div className="px-4 pt-4 mb-6">
        <button
          onClick={() => navigate('/mobile/scan')}
          data-testid="dash-scan-qr-btn"
          className="w-full flex items-center justify-center gap-3 bg-[var(--volt)] text-black rounded-[13px] active:scale-[0.99] transition-transform"
          style={{ height: 62, border: '1px solid var(--volt-edge)', fontWeight: 800 }}
        >
          <QrCode className="w-6 h-6" />
          <span className="text-[15px] tracking-[0.02em]">SCAN ASSET QR CODE</span>
        </button>
      </div>

      {/* Services due — self-allocate scheduled maintenance */}
      {servicesDueCount > 0 && (
        <div className="px-4 mb-6">
          <button
            onClick={() => navigate('/mobile/services')}
            data-testid="dash-services-due-card"
            className="w-full flex items-center gap-3 rounded-[13px] px-4 active:scale-[0.99] transition-transform text-left"
            style={{ height: 66, background: 'var(--surface-card)', border: `1px solid ${servicesOverdueCount > 0 ? 'var(--overdue-edge)' : 'var(--border)'}` }}
          >
            <div className="flex items-center justify-center rounded-[11px] shrink-0"
              style={{ width: 44, height: 44, background: servicesOverdueCount > 0 ? 'var(--overdue-bg)' : 'var(--surface-sunken)' }}>
              <CalendarClock className="w-6 h-6" style={{ color: servicesOverdueCount > 0 ? 'var(--overdue-ink)' : 'var(--ink-secondary)' }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-extrabold text-[var(--ink-primary)] leading-tight">Services due</div>
              <div className="text-[12.5px] text-[var(--ink-secondary)]">
                {servicesOverdueCount > 0
                  ? <><span className="font-semibold text-[var(--overdue-ink)]">{servicesOverdueCount} overdue</span> · {servicesDueCount} total — tap to claim</>
                  : <>{servicesDueCount} due soon — tap to claim</>}
              </div>
            </div>
            <span className="font-mono text-[20px] font-bold text-[var(--ink-primary)]">{servicesDueCount}</span>
          </button>
        </div>
      )}

      {/* In Progress */}
      {inProgressWorkOrders.length > 0 && (
        <div className="px-4 mb-6">
          <SectionLabel>In Progress</SectionLabel>
          {inProgressWorkOrders.map((wo) => (
            <WorkOrderCard key={wo.id} workOrder={wo} />
          ))}
        </div>
      )}

      {/* My Work Orders */}
      <div className="px-4 mb-6">
        <SectionLabel
          right={
            overdueCount > 0 ? (
              <span className="text-[12px] font-semibold text-[var(--overdue-ink)]">
                {overdueCount} of {activeWorkOrders.length} overdue
              </span>
            ) : (
              <span className="font-mono text-[11px] text-[var(--ink-tertiary)]">{activeWorkOrders.length}</span>
            )
          }
        >
          My Work Orders
        </SectionLabel>

        {pendingWorkOrders.length > 0 ? (
          pendingWorkOrders.slice(0, 3).map((wo) => <WorkOrderCard key={wo.id} workOrder={wo} />)
        ) : (
          <MobileCard className="text-center py-6">
            <CheckCircle className="w-8 h-8 text-[var(--pass-ink)] mx-auto mb-2" />
            <p className="text-[var(--ink-tertiary)]">All caught up!</p>
          </MobileCard>
        )}

        {activeWorkOrders.length > 3 && (
          <TextButton testid="dash-view-all-wo" onClick={() => navigate('/mobile/work-orders')}>
            View all {activeWorkOrders.length} work orders
          </TextButton>
        )}
      </div>

      {/* Inspections Due */}
      {data.inspections_due?.length > 0 && (
        <div className="px-4 mb-6">
          <SectionLabel
            right={
              <span className="font-mono text-[11px] text-[var(--ink-tertiary)]">{data.inspections_due.length}</span>
            }
          >
            Inspections Due
          </SectionLabel>

          {data.inspections_due.slice(0, 2).map((item, idx) => (
            <InspectionDueCard key={idx} asset={item.asset} template={item.template} />
          ))}

          {data.inspections_due.length > 2 && (
            <TextButton testid="dash-view-all-insp" onClick={() => navigate('/mobile/inspections')}>
              View all {data.inspections_due.length} inspections
            </TextButton>
          )}
        </div>
      )}

      {/* Open Defects */}
      {data.recent_defects?.length > 0 && (
        <div className="px-4 mb-6">
          <SectionLabel
            right={<span className="text-[12px] text-[var(--ink-tertiary)]">{openDefectCount} open</span>}
          >
            Open Defects
          </SectionLabel>
          <MobileCard padding="small">
            {data.recent_defects.slice(0, 3).map((defect, idx) => (
              <DefectRow key={defect.id} defect={defect} first={idx === 0} />
            ))}
          </MobileCard>
          <TextButton testid="dash-view-all-defects" onClick={() => navigate('/mobile/defects')}>
            View all {data.recent_defects.length} defects
          </TextButton>
        </div>
      )}

      {/* Secondary action — Report New Defect */}
      <div className="px-4 pb-4">
        <button
          onClick={() => navigate('/mobile/defects/new')}
          data-testid="dash-report-defect-btn"
          className="w-full flex items-center justify-center gap-2 bg-[var(--surface-card)] text-[var(--ink-primary)] rounded-[13px] active:scale-[0.99] transition-transform"
          style={{ height: 54, border: '1.5px solid #1A1A1A', fontWeight: 700 }}
        >
          <AlertTriangle className="w-[18px] h-[18px]" />
          <span className="text-[14px]">Report New Defect</span>
        </button>
      </div>

      <MobileNav />
    </div>
  );
};

export default MobileDashboard;
